import { useState, useEffect } from 'react';
import Dialog from '@components/ui/Dialog.js';
import Input from '@components/ui/Input.js';
import Button from '@components/ui/Button.js';
import {
  Calendar,
  DollarSign,
  Tag,
  CheckCircle2,
  XCircle,
  Clock,
  Check,
} from 'lucide-react';
import { cn } from '@utils/Cn.js';
import {
  CLAIM_TYPES,
} from '@repo/constants';
import type { PolicyClaim, ClaimStatus, ClaimFormData } from '../types/index.js';
import {
  useCreatePolicyClaimMutation,
  useUpdatePolicyClaimMutation,
} from '../hooks/usePolicyClaimsQuery.js';

interface AddEditClaimModalProps {
  open: boolean;
  onClose: () => void;
  policyId: string;
  claim?: PolicyClaim | null;
}

const PRIMARY_STATUSES: { key: ClaimStatus; label: string; icon: typeof Clock; color: string }[] = [
  { key: 'CLAIM_INITIATED', label: 'Claim Initiated', icon: Clock, color: 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300' },
  { key: 'APPROVED', label: 'Approved', icon: CheckCircle2, color: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300' },
  { key: 'REJECTED', label: 'Rejected', icon: XCircle, color: 'border-rose-500/30 bg-rose-500/10 text-rose-800 dark:text-rose-300' },
  { key: 'SETTLED', label: 'Settled / Closed', icon: Check, color: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-800 dark:text-cyan-300' },
];

export default function AddEditClaimModal({
  open,
  onClose,
  policyId,
  claim,
}: AddEditClaimModalProps) {
  const isEdit = !!claim;
  const createMutation = useCreatePolicyClaimMutation(policyId);
  const updateMutation = useUpdatePolicyClaimMutation(policyId);

  const [formData, setFormData] = useState<ClaimFormData>({
    claimNumber: '',
    claimStatus: 'CLAIM_INITIATED',
    claimType: '',
    claimDate: new Date().toISOString().slice(0, 10),
    incidentDate: '',
    claimAmount: '',
    approvedAmount: '',
    settledAmount: '',
    settlementDate: '',
    rejectionReason: '',
    remarks: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (claim) {
      setFormData({
        claimNumber: claim.claimNumber ?? '',
        claimStatus: claim.claimStatus,
        claimType: claim.claimType ?? '',
        claimDate: claim.claimDate ? new Date(claim.claimDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
        incidentDate: claim.incidentDate ? new Date(claim.incidentDate).toISOString().slice(0, 10) : '',
        claimAmount: claim.claimAmount != null ? String(claim.claimAmount) : '',
        approvedAmount: claim.approvedAmount != null ? String(claim.approvedAmount) : '',
        settledAmount: claim.settledAmount != null ? String(claim.settledAmount) : '',
        settlementDate: claim.settlementDate ? new Date(claim.settlementDate).toISOString().slice(0, 10) : '',
        rejectionReason: claim.rejectionReason ?? '',
        remarks: claim.remarks ?? '',
      });
    } else {
      setFormData({
        claimNumber: '',
        claimStatus: 'CLAIM_INITIATED',
        claimType: '',
        claimDate: new Date().toISOString().slice(0, 10),
        incidentDate: '',
        claimAmount: '',
        approvedAmount: '',
        settledAmount: '',
        settlementDate: '',
        rejectionReason: '',
        remarks: '',
      });
    }
    setErrors({});
  }, [claim, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.claimDate) {
      newErrors.claimDate = 'Claim date is required';
    }

    if (formData.claimStatus === 'REJECTED' && !formData.rejectionReason?.trim()) {
      newErrors.rejectionReason = 'Rejection reason is required when status is Rejected';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      claimNumber: formData.claimNumber?.trim() || null,
      claimStatus: formData.claimStatus,
      claimType: formData.claimType?.trim() || null,
      claimDate: formData.claimDate,
      incidentDate: formData.incidentDate || null,
      claimAmount: formData.claimAmount ? Number(formData.claimAmount) : null,
      approvedAmount: formData.approvedAmount ? Number(formData.approvedAmount) : null,
      settledAmount: formData.settledAmount ? Number(formData.settledAmount) : null,
      settlementDate: formData.settlementDate || null,
      rejectionReason: formData.rejectionReason?.trim() || null,
      remarks: formData.remarks?.trim() || null,
    };

    if (isEdit && claim) {
      updateMutation.mutate(
        { claimId: claim.id, data: payload },
        {
          onSuccess: () => {
            onClose();
          },
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          onClose();
        },
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Claim Record' : 'File Insurance Claim'}
      description="Track claim progress and financial settlements for this policy."
      sheet
      className="sm:w-[560px]"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Status selection buttons */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-ink-soft">
            Claim Status <span className="text-red-edge">*</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PRIMARY_STATUSES.map((item) => {
              const active = formData.claimStatus === item.key || (item.key === 'SETTLED' && formData.claimStatus === 'CLOSED');
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, claimStatus: item.key }));
                    if (errors.rejectionReason && item.key !== 'REJECTED') {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.rejectionReason;
                        return next;
                      });
                    }
                  }}
                  className={cn(
                    'flex flex-col items-center justify-center p-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer gap-1 text-center',
                    active
                      ? `${item.color} shadow-xs ring-2 ring-slate/20`
                      : 'border-line bg-paper/60 text-ink-soft hover:bg-paper hover:text-ink',
                  )}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Primary Row: Claim Date (Mandatory) & Claim Number (Optional) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Claim Date (Mandatory)"
            type="date"
            required
            value={formData.claimDate}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, claimDate: e.target.value }));
              if (errors.claimDate) {
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.claimDate;
                  return next;
                });
              }
            }}
            error={errors.claimDate}
            leftElement={<Calendar size={14} />}
          />

          <Input
            label="Claim Number / Ref (Optional)"
            placeholder="e.g. CLM-2026-001"
            value={formData.claimNumber}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, claimNumber: e.target.value }));
            }}
            leftElement={<Tag size={14} />}
          />
        </div>

        {/* Second Row: Claim Type & Incident Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-ink-soft">
              Claim Type (Optional)
            </label>
            <select
              value={formData.claimType}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, claimType: e.target.value }));
              }}
              className="w-full h-11 rounded-xl border border-line-strong bg-surface px-3 font-sans text-sm text-ink outline-none transition-all focus:border-slate focus:shadow-[0_0_0_3px_rgba(15,118,110,0.15)]"
            >
              <option value="">Select claim type...</option>
              {CLAIM_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Incident Date (Optional)"
            type="date"
            value={formData.incidentDate}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, incidentDate: e.target.value }));
            }}
            leftElement={<Calendar size={14} />}
          />
        </div>

        {/* Third Row: Financial Amounts */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            label="Claim Amount (₹)"
            type="number"
            placeholder="e.g. 50000"
            value={formData.claimAmount}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, claimAmount: e.target.value }));
            }}
            leftElement={<DollarSign size={14} />}
          />

          <Input
            label="Approved Amount (₹)"
            type="number"
            placeholder="e.g. 45000"
            value={formData.approvedAmount}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, approvedAmount: e.target.value }));
            }}
            leftElement={<DollarSign size={14} />}
          />

          <Input
            label="Settled Amount (₹)"
            type="number"
            placeholder="e.g. 45000"
            value={formData.settledAmount}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, settledAmount: e.target.value }));
            }}
            leftElement={<DollarSign size={14} />}
          />
        </div>

        {/* Settlement Date (If settled or optional) */}
        {(formData.claimStatus === 'SETTLED' || formData.claimStatus === 'CLOSED' || formData.settledAmount) && (
          <Input
            label="Settlement Date (Optional)"
            type="date"
            value={formData.settlementDate}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, settlementDate: e.target.value }));
            }}
            leftElement={<Calendar size={14} />}
          />
        )}

        {/* Conditional Rejection Reason */}
        {formData.claimStatus === 'REJECTED' && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-rose-700 dark:text-rose-400">
              Reason for Rejection <span className="text-red-edge">*</span>
            </label>
            <textarea
              rows={2}
              required
              placeholder="e.g. Document mismatch, exclusions applied, pre-existing condition..."
              value={formData.rejectionReason}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, rejectionReason: e.target.value }));
                if (errors.rejectionReason) {
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.rejectionReason;
                    return next;
                  });
                }
              }}
              className={cn(
                'w-full rounded-xl border bg-surface p-3 font-sans text-sm text-ink outline-none transition-all placeholder:text-ink-faint focus:border-rose-500 focus:shadow-[0_0_0_3px_rgba(244,63,94,0.15)]',
                errors.rejectionReason ? 'border-rose-500' : 'border-rose-500/30 bg-rose-500/5',
              )}
            />
            {errors.rejectionReason && (
              <p className="text-[11px] font-semibold text-red-fg">{errors.rejectionReason}</p>
            )}
          </div>
        )}

        {/* Remarks / Notes */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-ink-soft">
            Remarks & Notes (Optional)
          </label>
          <textarea
            rows={2}
            placeholder="Add any internal notes, surveyor info, or insurer communication..."
            value={formData.remarks}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, remarks: e.target.value }));
            }}
            className="w-full rounded-xl border border-line-strong bg-surface p-3 font-sans text-sm text-ink outline-none transition-all placeholder:text-ink-faint focus:border-slate focus:shadow-[0_0_0_3px_rgba(15,118,110,0.15)]"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" variant="outline" size="md" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={isPending}>
            {isPending ? 'Saving...' : isEdit ? 'Update Claim' : 'Record Claim'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
